import React, { useState } from 'react';
import { Music } from 'lucide-react';
import { Choir } from '../../types/Choir';
import { apiClient } from '../../config/api';
import ChoirService from '../../services/ChoirService';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import ChoirForm from './ChoirForm';
import { choirTableConfig } from './ChoirTableConfig';

interface ChoirFormData {
  name: string;
  description: string;
  skillLevel?: string;
  auditionRequired: boolean;
  category?: string;
  isActive: boolean;
}

const EnhancedChoirManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingChoir, setEditingChoir] = useState<Choir | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const actions = {
    handleCreate: () => {
      setEditingChoir(null);
      setShowForm(true);
    },

    handleEdit: (choir: Choir) => {
      setEditingChoir(choir);
      setShowForm(true);
    },

    handleView: (choir: Choir) => {
      // For now, view is the same as edit
      // In the future, you could implement a read-only view
      setEditingChoir(choir);
      setShowForm(true);
    }
  };

  const handleFormSubmit = async (formData: ChoirFormData, imageFiles?: File[]) => {
    try {
      setIsLoading(true);

      let updatedChoir;
      if (editingChoir) {
        updatedChoir = await apiClient.put(`/api/admin/choirs/${editingChoir.id}`, formData);
      } else {
        updatedChoir = await apiClient.post('/api/admin/choirs', formData);
      }

      // If there are image files to upload and we have a choir ID
      if (imageFiles && imageFiles.length > 0 && (editingChoir?.id || updatedChoir?.id)) {
        const choirId = editingChoir?.id || updatedChoir?.id;
        console.log(`Uploading ${imageFiles.length} images for choir ${choirId}`);

        // Upload images one by one to avoid race conditions
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];
          console.log(`Uploading image ${i + 1}/${imageFiles.length}: ${file.name}`);
          try {
            await ChoirService.uploadChoirImage(choirId, file);
            console.log(`Image ${i + 1} uploaded successfully`);
          } catch (uploadError) {
            console.error(`Failed to upload image ${i + 1}:`, uploadError);
            // Continue with other images even if one fails
          }
        }
        console.log('All image uploads completed');
      }

      setShowForm(false);
      setEditingChoir(null);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to save choir:', error);
      alert('Failed to save choir');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (choirId: string) => {
    if (!window.confirm('Are you sure you want to delete this choir?')) return;

    try {
      await apiClient.delete(`/api/admin/choirs/${choirId}`);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh
    } catch (error) {
      console.error('Failed to delete choir:', error);
      alert('Failed to delete choir');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingChoir(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Manage Choirs"
          icon={<Music size={32} />}
          subtitle="Create and manage choir groups and ensembles"
        />

        <div className="flex justify-end">
          <button
            onClick={actions.handleCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Music size={20} />
            Add New Choir
          </button>
        </div>

        {/* Admin Table */}
        <AdminTable
          onView={actions.handleView}
          onEdit={actions.handleEdit}
          onDelete={handleDelete}
          config={choirTableConfig}
          refreshTrigger={refreshTrigger}
        />

        {/* Choir Form Modal */}
        <ChoirForm
          item={editingChoir || undefined}
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default EnhancedChoirManagement;
